import Unit from "./unit.js";

class ConversionResult{
    constructor(baseValue, convertedValue, quantity, baseUnit, targetUnit){
        this.baseValue = baseValue;
        this.value = convertedValue;
        this.quantity = quantity;
        this.baseUnit = baseUnit;
        this.targetUnit = targetUnit;
    }

    /**
     * Returns true if given instance of ConversionResult has numeric conversion result
     */
    hasValue(){
        return typeof this.value === 'number';
    }

    /**
     * Method that returns rounded value
     *
     * @param {Integer} fractionalDigits - count of numbers after coma
     * @returns {String|*}
     */
    round(fractionalDigits) {
        if (!this.hasValue()){
            return this.value;
        }

        return ConversionResult.round(this.value, fractionalDigits);
    }

    /**
     * Allows to stringify converted value in various ways
     * @param {*} noValueFallback - optional, value which will be returned when ConversionValue has no value set
     * @param {Integer} fractionalDigits  - optional, number of desired decimal places for rounding
     * @param {String} thousandSeparator - optional, character used as thousand separator
     * @param {String} decimalSeparator - optional, character used as CUSTOM decimal separator
     * @param {Boolean} fractionalDigitsPadding - optional, true to provide always the same number of decimals, defined by fractionalDigits property
     */
    format(noValueFallback, fractionalDigits, thousandSeparator, decimalSeparator, fractionalDigitsPadding){
        if (!this.hasValue()){
            return noValueFallback || ( typeof this.value === 'undefined' ? '': this.value.toString() );
        }

        let valueToFormat = this.value;
        let fractionalPaddingLength = undefined;

        if (typeof(fractionalDigits) === 'number'){
            valueToFormat = this.round(fractionalDigits);

            if (fractionalDigitsPadding){
                fractionalPaddingLength = fractionalDigits;
            }
        }

        if (this.targetUnit && this.targetUnit.hasFormatter()){
            return this.targetUnit.formatter.stringify(valueToFormat, thousandSeparator, decimalSeparator, fractionalPaddingLength);
        }

        return ConversionResult.stringify(valueToFormat, thousandSeparator, decimalSeparator, fractionalPaddingLength);
    }

    /**
     * Returns unit symbol with desired index
     * @param {Integer} unitSymbolIndex - optinal index of target unit symbol, see given unit symbols property for possible options (default - index 0)
     */
    getTargetUnitSymbol(unitSymbolIndex){
        return ConversionResult.getUnitSymbol(this.targetUnit, unitSymbolIndex);
    }

    /**
     * Returns unit symbol with desired index
     * @param {Integer} unitSymbolIndex - optinal index of base unit symbol, see given unit symbols property for possible options (default - index 0)
     */
    getBaseUnitSymbol(unitSymbolIndex){
        return ConversionResult.getUnitSymbol(this.baseUnit, unitSymbolIndex);
    }

    /**
     * Allows to format converted value with unit symbol appended to final number
     * @param {*} noValueFallback - optional, value which will be returned when ConversionValue has no value set
     * @param {Integer} fractionalDigits  - optional, number of desired decimal places for rounding
     * @param {String} thousandSeparator - optional, character used as thousand separator
     * @param {String} decimalSeparator - optional, character used as CUSTOM decimal separator
     * @param {String} symbolPrefix  - optional, string which will separate converted value and unit symbol, space is used for default
     * @param {String} symbolSuffix - optional, string which will follow unit symbol, empty string is used for default
     * @param {Integer} unitSymbolIndex - optional index of target unit symbol, see given unit symbols property for possible options (default - index 0)
     * @param {Boolean} fractionalDigitsPadding - optional, true to provide always the same number of decimals, defined by fractionalDigits property
     */
    formatWithSymbol(noValueFallback, fractionalDigits, thousandSeparator, decimalSeparator, symbolPrefix, symbolSuffix, unitSymbolIndex, fractionalDigitsPadding){
        if (!this.hasValue()){
            return noValueFallback || ( typeof this.value === 'undefined' ? '': this.value.toString() );
        }

        let stringified = this.format(noValueFallback, fractionalDigits, thousandSeparator, decimalSeparator, fractionalDigitsPadding);

        if (this.targetUnit && this.targetUnit.hasFormatter()){
            return this.targetUnit.formatter.stringifyUnitSymbol(stringified, symbolPrefix, symbolSuffix, this.targetUnit, unitSymbolIndex);
        }

        return ConversionResult.stringifyUnitSymbol(stringified, symbolPrefix, symbolSuffix, this.targetUnit, unitSymbolIndex);
    }

    static stringifyUnitSymbol(stringified, symbolPrefix, symbolSuffix, unit, unitSymbolIndex){
        return `${stringified}${
            typeof (symbolPrefix) === 'undefined' ? ' ' : symbolPrefix }${ConversionResult.getUnitSymbol(unit, unitSymbolIndex || 0) || ''
        }${symbolSuffix || ''}`;
    }

    static getUnitSymbol(unit, unitSymbolIndex){
        if (!(unit instanceof Unit) ){
            return '';
        }

        return unit.getSymbol(unitSymbolIndex || 0) || '';
    }

    /**
     * allows to stringify numbers including some custom formatting
     * 
     * @param {Number} value - value to convert
     * @param {String} thousandSeparator - custom thousands separator, default is no separator
     * @param {String} decimalSeparator - custom decimal point, default is "."
     * @param {Number} fractionalDigitsPadLength - length of desired padding of fractional part
     * @returns {String}
     */
    static stringify(value, thousandSeparator, decimalSeparator, fractionalDigitsPadLength) {
        if (typeof (value) !== 'number') {
            throw TypeError(`value must contain valid number`);
        }

        const isNegative = value < 0;
        const absoluteValue = Math.abs(value);
        let output = absoluteValue.toString();

        if (typeof(fractionalDigitsPadLength) === 'number'){
            // this method is intended and preferred over +1.02/-1.02 and 1.02/-1.0
            const integer = isNegative ? Math.ceil(value): Math.floor(value);
            let fractional = Math.abs(value - integer);
            output = Math.abs(integer).toString() + fractional.toFixed(fractionalDigitsPadLength).substring(1);
        }

        if (typeof thousandSeparator === 'string' && thousandSeparator.length > 0) {
            //find the dot position
            let stringified = output;
            if (typeof decimalSeparator === 'string'){
                stringified = stringified.replace('.', decimalSeparator);
            }

            let decimalSepPosition = stringified.indexOf(decimalSeparator || '.');
            // todo - it won't work, need to fined better way to do it...
            
            if (decimalSepPosition == -1 && stringified.length > 3) {
                // so we have no decimal separator and number is greater than 999...
                decimalSepPosition = stringified.length;
            }
            
            if (decimalSepPosition > 3) {
                output = stringified.substr(decimalSepPosition, stringified.length);
                for (let i = decimalSepPosition - 1; i >= 0; i--) {
                    output = stringified[i] + output;
                    if (i && (i - decimalSepPosition) % 3 == 0) {
                        output = thousandSeparator + output;
                    }
                }
            }
        }
        else if (typeof decimalSeparator === 'string'){
            output = output.replace('.', decimalSeparator);
        }

        if (isNegative) {
            output = '-' + output;
        }

        return output + '';
    }
    
    static round(value, fractionalDigits){
        if (typeof (fractionalDigits) !== 'number') {
            throw TypeError(`fractionalDigits must contain valid number`);
        }

        if (typeof (value) !== 'number') {
            throw TypeError(`value must contain valid number`);
        }

        const multiplier = Math.pow(10, fractionalDigits);
        return Math.round(value * multiplier) / multiplier;
    }
}

export default ConversionResult;